import { useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useRouter } from '@/context/RouterContext';
import { Header } from '@/components/navigation/Header';

import { SettingsTab } from '@/components/settings/SettingsTab';
import { RacesTab } from '@/components/races/RacesTab';
import { TracksTab } from '@/components/tracks/TracksTab';
import { CategoriesTab } from '@/components/categories/CategoriesTab';
import { DriversTab } from '@/components/drivers/DriversTab';
import { ProfilesTab } from '@/components/profiles/ProfilesTab';
import { UsersTab } from '@/components/users/UsersTab';
import { BugsTab } from '@/components/bugs/BugsTab';
import { MonitoringTab } from '@/components/monitoring/MonitoringTab';

import type { EditorTab } from '@/types/routes';
import { EDITOR_TABS } from '@/types/routes';
import { useAuth } from '@/context/AuthContext';
import { LoginPage } from '@/pages/LoginPage';
import { AccessDeniedPage } from '@/pages/AccessDeniedPage';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { useToast } from '@/context/ToastContext';
import { useI18n } from '@/context/I18nContext';
import { ErrorBoundary } from '@shared/ErrorBoundary';
import { OfflineGuard } from '@/components/navigation/OfflineGuard';
import { TermsAcceptanceGate } from '@shared/auth/TermsAcceptanceGate';
import { Markdown } from '@com.yanicksenn/shared';

const TAB_ORDER: Partial<Record<EditorTab, number>> = Object.fromEntries(
  EDITOR_TABS.map((t, i) => [t, i])
);

function renderTab(tab: EditorTab) {
  switch (tab) {
    case 'races':         return <RacesTab />;
    case 'tracks':        return <TracksTab />;
    case 'categories':    return <CategoriesTab />;
    case 'drivers':       return <DriversTab />;
    case 'profiles':      return <ProfilesTab />;
    case 'users':         return <UsersTab />;
    case 'bugs':          return <BugsTab />;
    case 'monitoring':    return <MonitoringTab />;
    case 'settings':      return <SettingsTab />;
    case 'login':         return <LoginPage />;
    default:              return <RacesTab />;
  }
}

export default function App() {
  const { tab } = useRouter();
  const { user, isLoading } = useAuth();
  const { showToast } = useToast();
  const { t } = useI18n();
  const prevTabRef = useRef<EditorTab>(tab);
  const directionRef = useRef(1);

  // Listen for auto-reported bug events from the RPC client
  useEffect(() => {
    const handler = () => showToast(t('error.autoReported'), 'error');
    window.addEventListener('bug:auto-reported', handler);
    return () => window.removeEventListener('bug:auto-reported', handler);
  }, [showToast, t]);

  if (tab !== prevTabRef.current) {
    const currentOrder = TAB_ORDER[tab] ?? 99;
    const prevOrder = TAB_ORDER[prevTabRef.current] ?? 99;
    directionRef.current = currentOrder > prevOrder ? 1 : -1;
    prevTabRef.current = tab;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  // Not authenticated → force login page
  if (!user && tab !== 'login') {
    window.location.hash = '#/login';
    return null;
  }

  // Authenticated but on login page → redirect to home
  if (user && tab === 'login') {
    window.location.hash = '#/';
    return null;
  }

  // Authenticated user who has not accepted the latest Terms & Conditions
  if (user && user.termsAccepted === false) {
    return (
      <TermsAcceptanceGate
        renderMarkdown={(md) => <Markdown content={md} />}
      />
    );
  }

  // Authenticated but not admin → access denied
  if (user && !user.isAdmin) {
    return <AccessDeniedPage />;
  }

  // Login page
  if (tab === 'login') {
    return <LoginPage />;
  }

  return (
    <div className="min-h-screen flex flex-col pt-[60px] sm:pt-[52px]">
      <Header />

      <main className="flex-1 max-w-[960px] mx-auto w-full px-5 sm:px-8 py-8">
        <AnimatePresence mode="popLayout" custom={directionRef.current}>
          <motion.div
            key={tab}
            custom={directionRef.current}
            initial={{ x: directionRef.current * 20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: directionRef.current * -20, opacity: 0, transition: { duration: 0.1 } }}
            transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <ErrorBoundary
              title={t('errorBoundary.title')}
              message={t('errorBoundary.message')}
              reloadLabel={t('errorBoundary.reload')}
              resetLabel={t('errorBoundary.reset')}
              showDetailsLabel={t('errorBoundary.showDetails')}
              hideDetailsLabel={t('errorBoundary.hideDetails')}
            >
              <OfflineGuard>
                {renderTab(tab)}
              </OfflineGuard>
            </ErrorBoundary>
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
}
